/**
 * Face-type coloring utility for polyhedra
 *
 * Analyzes BufferGeometry to detect polygon face types and assigns
 * vertex colors based on the number of sides each face has.
 */

import type { BufferGeometry } from 'three';
import { getColorPalette, type ColorFunction } from '@plugins/video/colorPalettes';

// Map face type (vertex count) to palette position
const faceTypeToT: Record<number, number> = {
  3: 0.15,   // triangles
  4: 0.40,   // squares
  5: 0.60,   // pentagons
  6: 0.75,   // hexagons
  8: 0.90,   // octagons
  10: 0.50,  // decagons
  12: 0.30,  // dodecagons
};

type ThreeModule = typeof import('three');

/**
 * Add face-type vertex colors to a BufferGeometry.
 * Groups coplanar adjacent triangles into faces and colors by face type.
 *
 * @param geometry - The BufferGeometry to color
 * @param three - The Three.js module
 * @param paletteName - Optional palette name (defaults to 'primary')
 */
export function addFaceTypeColors(
  geometry: BufferGeometry,
  three: ThreeModule,
  paletteName: string = 'primary'
): void {
  if (!geometry) return;

  const posAttr = geometry.getAttribute('position');
  if (!posAttr) return;

  const triCount = posAttr.count / 3;
  if (triCount < 1) return;

  const palette = getColorPalette(paletteName);
  const colors = new Float32Array(posAttr.count * 3);

  // Build edge-to-triangle mapping and get triangle normals
  const edgeToTris = new Map<string, number[]>();
  const triNormals: { x: number; y: number; z: number }[] = [];

  const getVertKey = (i: number) => {
    const x = posAttr.getX(i).toFixed(4);
    const y = posAttr.getY(i).toFixed(4);
    const z = posAttr.getZ(i).toFixed(4);
    return `${x},${y},${z}`;
  };

  const getEdgeKey = (v1: string, v2: string) => v1 < v2 ? `${v1}|${v2}` : `${v2}|${v1}`;

  // First pass: build edge map and compute normals
  for (let t = 0; t < triCount; t++) {
    const i0 = t * 3, i1 = t * 3 + 1, i2 = t * 3 + 2;
    const v0 = getVertKey(i0), v1 = getVertKey(i1), v2 = getVertKey(i2);

    // Add edges
    for (const [a, b] of [[v0, v1], [v1, v2], [v2, v0]]) {
      const key = getEdgeKey(a, b);
      if (!edgeToTris.has(key)) edgeToTris.set(key, []);
      edgeToTris.get(key)!.push(t);
    }

    // Compute normal
    const ax = posAttr.getX(i1) - posAttr.getX(i0);
    const ay = posAttr.getY(i1) - posAttr.getY(i0);
    const az = posAttr.getZ(i1) - posAttr.getZ(i0);
    const bx = posAttr.getX(i2) - posAttr.getX(i0);
    const by = posAttr.getY(i2) - posAttr.getY(i0);
    const bz = posAttr.getZ(i2) - posAttr.getZ(i0);
    let nx = ay * bz - az * by;
    let ny = az * bx - ax * bz;
    let nz = ax * by - ay * bx;
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len > 0) { nx /= len; ny /= len; nz /= len; }
    triNormals.push({ x: nx, y: ny, z: nz });
  }

  // Group coplanar adjacent triangles into faces
  const triFaceId = new Array(triCount).fill(-1);
  let faceId = 0;
  const faceTriCounts: number[] = [];

  const normalsMatch = (n1: { x: number; y: number; z: number }, n2: { x: number; y: number; z: number }) => {
    const dot = n1.x * n2.x + n1.y * n2.y + n1.z * n2.z;
    return Math.abs(dot) > 0.999; // Nearly parallel
  };

  for (let t = 0; t < triCount; t++) {
    if (triFaceId[t] !== -1) continue;

    // BFS to find all coplanar connected triangles
    const queue = [t];
    triFaceId[t] = faceId;
    let count = 0;

    while (queue.length > 0) {
      const curr = queue.shift()!;
      count++;

      const i0 = curr * 3, i1 = curr * 3 + 1, i2 = curr * 3 + 2;
      const v0 = getVertKey(i0), v1 = getVertKey(i1), v2 = getVertKey(i2);

      for (const [a, b] of [[v0, v1], [v1, v2], [v2, v0]]) {
        const key = getEdgeKey(a, b);
        const neighbors = edgeToTris.get(key) || [];
        for (const neighbor of neighbors) {
          if (triFaceId[neighbor] === -1 && normalsMatch(triNormals[curr], triNormals[neighbor])) {
            triFaceId[neighbor] = faceId;
            queue.push(neighbor);
          }
        }
      }
    }

    faceTriCounts.push(count);
    faceId++;
  }

  // Assign colors based on face type (tri count → vertex count)
  // n triangles in a fan = n+2 vertices for the polygon
  for (let t = 0; t < triCount; t++) {
    const face = triFaceId[t];
    const triCountInFace = faceTriCounts[face];
    const vertexCount = triCountInFace + 2; // Fan triangulation: n tris = n+2 verts

    const tValue = faceTypeToT[vertexCount] ?? 0.5;
    const [r, g, b] = palette(tValue);

    for (let v = 0; v < 3; v++) {
      const idx = t * 3 + v;
      colors[idx * 3] = r / 255;
      colors[idx * 3 + 1] = g / 255;
      colors[idx * 3 + 2] = b / 255;
    }
  }

  geometry.setAttribute('color', new three.Float32BufferAttribute(colors, 3));
}
